import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// ---- Super Admin: create/edit/delete reusable onboarding packs ----
export function PackManagerDialog({ onClose }) {
  const [packs, setPacks] = useState(null);
  const [resources, setResources] = useState([]);
  const [editing, setEditing] = useState(null); // null=list, {}=new, pack=edit
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/onboarding-packs").then((r) => setPacks(r.data)).catch(() => setPacks([]));
  useEffect(() => { load(); api.get("/resources").then((r) => setResources(r.data)).catch(() => {}); }, []);

  const del = async () => {
    try {
      await api.delete(`/onboarding-packs/${deleteTarget.id}`);
      toast.success("Pack deleted");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="pack-manager-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? (editing.id ? "Edit onboarding pack" : "New onboarding pack") : "Onboarding packs"}</DialogTitle>
          <DialogDescription>
            {editing ? "Name the pack and choose the resources it bundles." : "Reusable resource bundles you can apply to a client in one click."}
          </DialogDescription>
        </DialogHeader>

        {editing ? (
          <PackForm pack={editing} resources={resources} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        ) : !packs ? (
          <div className="py-8 text-center"><Icons.Loader2 className="mx-auto animate-spin text-[#FFB000]" size={22} /></div>
        ) : (
          <>
            <div className="space-y-2">
              {packs.length === 0 && (
                <div className="rounded-lg border border-dashed border-[#E4E8EF] py-8 text-center text-sm text-[#1E2D44]/50" data-testid="packs-empty">
                  No packs yet. Create your first onboarding pack.
                </div>
              )}
              {packs.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-lg border border-[#E4E8EF] px-3.5 py-3" data-testid={`pack-row-${p.id}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0B1729] text-[#FFC24D]"><Icons.PackageOpen size={17} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-[#0B1729]">{p.name}</div>
                    <div className="truncate text-xs text-[#1E2D44]/50">{p.resource_count} resource{p.resource_count === 1 ? "" : "s"}{p.description ? ` · ${p.description}` : ""}</div>
                  </div>
                  <button onClick={() => setEditing(p)} className="text-[#1E2D44]/40 hover:text-[#FFB000]" data-testid={`edit-pack-${p.id}`}><Icons.Pencil size={15} /></button>
                  <button onClick={() => setDeleteTarget(p)} className="text-[#1E2D44]/40 hover:text-[#C95555]" data-testid={`delete-pack-${p.id}`}><Icons.Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <DialogFooter className="sm:justify-between">
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button onClick={() => setEditing({})} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-pack-btn"><Icons.Plus size={15} className="mr-1" /> New pack</Button>
            </DialogFooter>
          </>
        )}

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="delete-pack-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this pack?</AlertDialogTitle>
              <AlertDialogDescription>"{deleteTarget?.name}" will be removed. Resources already assigned to clients are not affected.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={del} className="bg-[#C95555] hover:bg-[#b34848]" data-testid="confirm-delete-pack">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

function PackForm({ pack, resources, onCancel, onSaved }) {
  const [name, setName] = useState(pack.name || "");
  const [description, setDescription] = useState(pack.description || "");
  const [ids, setIds] = useState(pack.resource_ids || (pack.resources || []).map((r) => r.id) || []);
  const [saving, setSaving] = useState(false);
  const toggle = (id) => setIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    if (!name.trim()) return toast.error("Add a pack name");
    setSaving(true);
    try {
      if (pack.id) await api.patch(`/onboarding-packs/${pack.id}`, { name, description, resource_ids: ids });
      else await api.post("/onboarding-packs", { name, description, resource_ids: ids });
      toast.success(pack.id ? "Pack updated" : "Pack created");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3.5">
      <div><Label>Pack name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="e.g. New client essentials" data-testid="pack-name-input" /></div>
      <div><Label>Description <span className="font-normal text-[#1E2D44]/45">(optional)</span></Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" rows={2} /></div>
      <div>
        <Label>Resources in this pack</Label>
        {resources.length === 0 ? (
          <p className="mt-1 text-xs text-[#1E2D44]/45">No resources available yet.</p>
        ) : (
          <div className="mt-1.5 max-h-52 space-y-1 overflow-y-auto rounded-lg border border-[#E4E8EF] p-2" data-testid="pack-resources">
            {resources.map((r) => (
              <label key={r.id} className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-[#F4F6F9]" data-testid={`pack-resource-${r.id}`}>
                <input type="checkbox" checked={ids.includes(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 accent-[#FFB000]" />
                <span className="min-w-0 flex-1 truncate text-sm text-[#0B1729]">{r.title}</span>
                <span className="shrink-0 text-[10px] text-[#1E2D44]/45">{r.category}</span>
              </label>
            ))}
          </div>
        )}
        <p className="mt-1 text-xs text-[#FFB000]">{ids.length} resource(s) in this pack</p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Back</Button>
        <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-pack-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save pack"}</Button>
      </DialogFooter>
    </div>
  );
}

// ---- Apply a saved pack to an existing client in one click ----
export function ApplyPackDialog({ org, onClose, onApplied }) {
  const [packs, setPacks] = useState(null);
  const [selected, setSelected] = useState(null);
  const [applying, setApplying] = useState(false);
  useEffect(() => { api.get("/onboarding-packs").then((r) => setPacks(r.data)).catch(() => setPacks([])); }, []);
  const apply = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/organisations/${org.id}/apply-pack`, { pack_id: selected });
      toast.success(data.added > 0 ? `Added ${data.added} resource(s) to ${org.company_name}` : "All pack resources were already assigned");
      onApplied?.();
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="apply-pack-dialog">
        <DialogHeader>
          <DialogTitle>Apply onboarding pack</DialogTitle>
          <DialogDescription>Assign a saved bundle of resources to {org.company_name} in one click.</DialogDescription>
        </DialogHeader>
        {!packs ? (
          <div className="py-8 text-center"><Icons.Loader2 className="mx-auto animate-spin text-[#FFB000]" size={22} /></div>
        ) : packs.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#1E2D44]/50">No onboarding packs yet. Create one from the Clients page first.</p>
        ) : (
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {packs.map((p) => (
              <button key={p.id} onClick={() => setSelected(p.id)} data-testid={`apply-pack-option-${p.id}`}
                className={`flex w-full items-center gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ${selected === p.id ? "border-[#FFB000] bg-[#FFB000]/8" : "border-[#E4E8EF] hover:border-[#FFC24D]"}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0B1729] text-[#FFC24D]"><Icons.PackageOpen size={17} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-[#0B1729]">{p.name}</span>
                  <span className="block truncate text-xs text-[#1E2D44]/50">{p.resource_count} resource{p.resource_count === 1 ? "" : "s"}</span>
                </span>
                {selected === p.id && <Icons.CheckCircle2 size={18} className="shrink-0 text-[#FFB000]" />}
              </button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} disabled={applying || !selected} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="confirm-apply-pack">{applying ? <Icons.Loader2 className="animate-spin" size={16} /> : "Apply pack"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
